import api from "@/lib/api";
import { loadProductCategories } from "@/lib/productCategories";
import { toAbsoluteUrl } from "@/lib/imageUrl";
import { industryCategorySlugs, industryProductIds } from "@/lib/industryContent";

// How an industry record is interpreted (lines, products, related industries, hero/overview/CTA text) lives in lib/industryContent.js.
export * from "@/lib/industryContent";

let cache = null;

/** Published industries in admin order, cached for the session (shared by the page, mega menu, homepage and footer). */
export function loadIndustries() {
  if (!cache) {
    cache = api.get("/industries")
      .then((r) => (r.data || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)))
      .catch((e) => { cache = null; throw e; });
  }
  return cache;
}

/** Forget the cached list — called after Admin saves/deletes an industry so the public pages in the same session show the change. */
export function invalidateIndustries() { cache = null; }

/** Product categories that contain products (title, slug, image, sample …), shared cache. */
export { loadProductCategories };

/** The industry's products (Admin → Industries → Products), in the saved order, resolved live from the Products collection. */
export async function loadIndustryProducts(industry) {
  const ids = industryProductIds(industry);
  if (!ids.length) return [];
  const list = (await api.get("/products", { params: { ids: ids.join(","), limit: 100 } })).data || [];
  const byId = new Map(list.map((p) => [p.id, p]));
  return ids.map((id) => byId.get(id)).filter((p) => p && p.name && p.status !== "draft");
}

/**
 * Real Shingare equipment photos for an industry (never stock): the industry's own products first (when supplied), then the photos of
 * the categories its product lines belong to. Used for the equipment strip / line-up next to the industry picture.
 */
export function industryImages(industry, categories = [], { products = [], max = 6 } = {}) {
  const bySlug = new Map(categories.map((c) => [c.slug, c]));
  const urls = products.map((p) => p.image).filter(Boolean);
  industryCategorySlugs(industry).forEach((slug) => {
    const c = bySlug.get(slug);
    const u = c && (c.image || c.sample?.image);
    if (u) urls.push(u);
  });
  return urls.map(toAbsoluteUrl).filter((u, i, a) => a.indexOf(u) === i).slice(0, max);
}

/** Stable "N product categories" count used on cards. */
export const categoryCount = (industry) => industryCategorySlugs(industry).length;

